import { useQuery } from "@tanstack/react-query";
import { getPrimaryList } from "@/services/shopping/lists/primary-list.service";
import { getSharedLists } from "@/services/shopping/lists/share.service";
import { useAuth } from "@/context/auth/AuthContext";
import useFeature from "@/hooks/useFeature";
import { HomeIcon, ListChecks, BarChart3Icon, Settings, Clock, Package, Users, Info, ShoppingCart, Crown, History, Store, WifiOff } from "lucide-react";

export interface SidebarLink {
  name: string;
  href: string;
  icon: any;
  fetchPrimary?: boolean;
  fetchPending?: boolean;
  fetchShared?: boolean;
}

export interface SidebarSection {
  section: string;
  items: SidebarLink[];
}

export const useSidebarLinks = () => {
  const { user } = useAuth();
  const { isFeatureEnabled } = useFeature();
  // Default to false if isPremium is undefined
  const isPremium = user?.isPremium || false;

  // Fetch primary list
  const { data: primaryList } = useQuery({
    queryKey: ['primary-list'],
    queryFn: getPrimaryList,
    enabled: !!user,
  });
  
  // Fetch pending shopping items
  const { data: shoppingList } = useQuery({
    queryKey: ['shopping-list', primaryList?.id],
    queryFn: () => primaryList?.id ? import('@/services/shopping/lists.service').then(m => m.getShoppingList(primaryList.id)) : null,
    enabled: !!primaryList?.id && !!user,
  });
  
  // Fetch shared lists to check if there are any
  const { data: sharedLists } = useQuery({
    queryKey: ['shared-lists'],
    queryFn: getSharedLists,
    enabled: !!user,
  });

  // Count pending items (not checked)
  const pendingItemsCount = shoppingList?.items?.filter(item => !item.checked)?.length || 0;
  
  // Check if there are shared lists
  const hasSharedLists = sharedLists && Array.isArray(sharedLists) && sharedLists.length > 0 || false;

  // Get links configuration based on user's premium status
  const links: SidebarSection[] = [
    {
      section: "FUNZIONI PRINCIPALI",
      items: [
        {
          name: "Home",
          href: "/",
          icon: HomeIcon,
        },
        {
          name: "Liste della spesa",
          href: "/lists",
          icon: ListChecks,
        },
        {
          name: "Shopping",
          href: "/shopping",
          icon: ShoppingCart,
          fetchPrimary: true,
          fetchPending: true,
        },
      ]
    },
    {
      section: "GESTIONE",
      items: [
        {
          name: "Negozi",
          href: "/shops",
          icon: Store,
        },
        {
          name: "Categorie",
          href: "/categories",
          icon: Package,
        },
        {
          name: "Prodotti",
          href: "/products",
          icon: Package,
        },
        {
          name: "Prodotti Ricorrenti",
          href: "/recurring-products",
          icon: Clock,
        },
        {
          name: "Storico Acquisti",
          href: "/shopping-history",
          icon: History,
        },
        // {
        //   name: "Condivisioni",
        //   href: "/shared-lists",
        //   icon: Users,
        //   fetchShared: true,
        // },
      ]
    },
    {
      section: "SISTEMA",
      items: [
        {
          name: "Impostazioni",
          href: "/settings",
          icon: Settings,
        },
        {
          name: "Modalità Offline",
          href: "/offline-config",
          icon: WifiOff,
        },
        // Mostra Premium solo se l'utente non è premium
        ...(isPremium ? [] : [{
          name: "Premium",
          href: "/premium",
          icon: Crown,
        }]),
      ]
    }
  ];

  return {
    links,
    pendingItemsCount,
    hasSharedLists,
    primaryList
  };
};
